import React, { useState, useCallback } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Button,
  Box,
  Select,
  Flex,
  Grid,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  HStack,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { Header, ColumnInfo } from "build-ts/metadata";
import { AggregationType, ColumnType, columnTypeToJSON, DataType, dataTypeToJSON, aggregationTypeToJSON } from "build-ts/enums";
import DataModelMenuComponent from "./DataModelMenuComponent";
import { BsFillInfoSquareFill } from "react-icons/bs";
import { CreateOrUpdateMetrics_Request } from "build-ts/interface";

interface AddDescriptionProps {
  header: Header;
  columns: ColumnInfo[];
  kgColumns: Header[];
  dependentKgColumns: Map<string, Header[]>;
  dependentMetrics: Map<string, Header[]>;
  handleHeaderChange: (property: string, value: string) => void;
  handleColumnChange: (index: number, property: "data_type" | "column_type" | "default_aggregation", value: string) => void;
  handleColumnHeaderChange: (
    index: number,
    property: string,
    value: string,
  ) => void;
  handleKGColumnChange: (
    index: number,
    kgColumnId: string,
    kgColumnHeader: Header | null
  ) => void;
  handleSubmit: () => void;
  updateFlag: boolean;
  buttonLoader: boolean;
  modelId?: string;
  refreshKGColumns: () => Promise<void>;
  initializeColumns?: () => Promise<void>;
  initializeMatchingColumns?: () => Promise<void>;
  handleCreateKGColumn: (name: string, columnType: ColumnType, index: number) => Promise<boolean>;
  handleAddMetricCreationRequest: (columnId: string, columnName: string) => void;
  handleMetricChange: (index: number, metricHeader: Header | null) => void;
  selectedMetrics: { [columnIndex: number]: Header };
  metricRequestList: CreateOrUpdateMetrics_Request[];
  onBack: () => void;
}

const AddDescription: React.FC<AddDescriptionProps> = ({
  header,
  columns,
  kgColumns,
  dependentKgColumns,
  dependentMetrics,
  handleHeaderChange,
  handleColumnChange,
  handleColumnHeaderChange,
  handleKGColumnChange,
  handleSubmit,
  updateFlag,
  buttonLoader,
  modelId,
  refreshKGColumns,
  initializeColumns,
  initializeMatchingColumns, 
  handleCreateKGColumn,
  handleAddMetricCreationRequest,
  handleMetricChange,
  selectedMetrics,
  metricRequestList,
  onBack
}) => {
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [selectedColumnForDescription, setSelectedColumnForDescription] = useState<number | null>(null);

  // Helper function to get combined metrics (existing + newly created)
  const getCombinedMetrics = useCallback((columnId: string): Header[] => {
    // Get existing metrics from dependencies
    const existingMetrics = dependentMetrics.get(columnId) || [];
    
    // Get newly created metrics from metricRequestList for this column
    const newMetrics: Header[] = [];
    metricRequestList.forEach(request => {
      request.metrics?.forEach((metric) => {
        // Check if this metric is for the current column
        const observationColumns = metric.definition?.observation_columns || [];
        const isForThisColumn = observationColumns.some((obsCol) => 
          obsCol.logical_column_header?.id === columnId
        );
        
        if (isForThisColumn && metric.header) {
          newMetrics.push(metric.header);
        }
      });
    });
    
    return [...existingMetrics, ...newMetrics];
  }, [dependentMetrics, metricRequestList]);

  const handleOpenDescriptionModal = (index: number) => {
    setSelectedColumnForDescription(index);
    setIsDescriptionModalOpen(true);
  };

  const handleCloseDescriptionModal = () => {
    setIsDescriptionModalOpen(false);
    setSelectedColumnForDescription(null);
  };

  const handleSaveDescription = (newDescription: string) => {
    if (selectedColumnForDescription !== null) {
      handleColumnHeaderChange(selectedColumnForDescription, "description", newDescription);
    }
  };

  const handleColumnTypeChange = async (index: number, property: "data_type" | "column_type" | "default_aggregation", value: string) => {
    // Handle the column change first
    handleColumnChange(index, property, value);
    
    // If column type is changed, re-initialize columns and matching columns
    if (property === "column_type") {
      if (initializeColumns) {
        await initializeColumns();
      }
      if (initializeMatchingColumns) {
        await initializeMatchingColumns();
      }
    }
  };

  return (
    <Box mt={4} ml={2}>
      <Box>
        <HStack spacing={3} justify="flex-end">
        <Button
          variant="outline"
          colorScheme="purple"
          onClick={onBack}
          leftIcon={<Box as="span">←</Box>}
        >
          Back
        </Button>
        <Button
          colorScheme="purple"
          onClick={handleSubmit}
          isLoading={buttonLoader}
          width="120px"
          mr={2}
        >
          {updateFlag ? "Update" : "Create"}
        </Button>
        </HStack>
      </Box>
      <Box>
      <Grid
        templateColumns={{ base: "1fr", md: "1fr 1fr" }}
        gap={3}
        mb={0}
        padding={2}
      >
        <FormControl id="header-name" isRequired>
          <Input
            placeholder="Enter header name"
            value={header?.name}
            onChange={(e) => handleHeaderChange("name", e.target.value)}
          />
        </FormControl>

        <FormControl id="header-description">
          <Input
            placeholder="Enter header description"
            value={header?.description || ""}
            onChange={(e) => handleHeaderChange("description", e.target.value)}
          />
        </FormControl>
      </Grid>

      <Flex alignItems="center" justifyContent="space-between" mb={3} padding={2} >
      </Flex>
      <Table size="sm" mt={4}>
        <Thead>
          <Tr>
            <Th>Column Name</Th>
            <Th>Data Type</Th>
            <Th>Column Type</Th>
            <Th>Default Aggregation</Th>
            <Th>KG Column</Th>
            <Th>Metric</Th>
          </Tr>
        </Thead>
        <Tbody>
          {columns.map((column, index) => (
            <Tr key={index}>
              <Td>
                <HStack>
                  <Tooltip
                      label={column.header?.description || "View/Edit Description"}
                      placement="top"
                      hasArrow
                      bg="gray.700"
                      color="white"
                      fontSize="sm"
                      p={2}
                      borderRadius="md"
                    >
                      <IconButton
                        aria-label="View/Edit Description"
                        icon={<BsFillInfoSquareFill />}
                        size="xs"
                        variant="ghost"
                        color="blue.500"
                        _hover={{ color: "purple.600", bg: "gray.50" }}
                        onClick={() => handleOpenDescriptionModal(index)}
                        boxSize={5}
                        alignSelf="center"
                      />
                  </Tooltip>
                  <Text m={0}>{column.header?.name || ""}</Text>
                </HStack> 
              </Td>
              <Td>
                <Select
                  value={dataTypeToJSON(column.data_type!) || ""}
                  onChange={(e) => handleColumnChange(index, "data_type", e.target.value)}
                >
                  <option value={dataTypeToJSON(DataType.VARCHAR)}>VARCHAR</option>
                  <option value={dataTypeToJSON(DataType.BOOLEAN)}>BOOLEAN</option>
                  <option value={dataTypeToJSON(DataType.INT32)}>INT32</option>
                  <option value={dataTypeToJSON(DataType.INT64)}>INT64</option>
                  <option value={dataTypeToJSON(DataType.DOUBLE)}>DOUBLE</option>
                  <option value={dataTypeToJSON(DataType.DATE)}>DATE</option>
                  <option value={dataTypeToJSON(DataType.DATETIME)}>DATETIME</option>
                </Select>
              </Td>
              <Td>
                <Select
                  value={columnTypeToJSON(column.column_type!) || ""}
                  onChange={(e) => handleColumnTypeChange(index, "column_type", e.target.value)}
                >
                  <option value={columnTypeToJSON(ColumnType.ATTRIBUTE)}>ATTRIBUTE</option>
                  {(column.data_type === DataType.INT32 || column.data_type === DataType.INT64 || column.data_type === DataType.DOUBLE) && ( 
                    <option value={columnTypeToJSON(ColumnType.MEASURE)}>MEASURE</option>
                  )}
                  {(column.data_type === DataType.DATE || column.data_type === DataType.DATETIME) && (
                    <option value={columnTypeToJSON(ColumnType.TEMPORAL)}>TEMPORAL</option>
                  )}
                </Select>
              </Td>
              <Td>
                {column.column_type === ColumnType.MEASURE ? (
                  <Select
                    value={aggregationTypeToJSON(column.default_aggregation!) || ""}
                    onChange={(e) => handleColumnChange(index, "default_aggregation", e.target.value)}
                  >
                    <option value={aggregationTypeToJSON(AggregationType.SUM)}>SUM</option>
                    <option value={aggregationTypeToJSON(AggregationType.AVERAGE)}>AVERAGE</option>
                    <option value={aggregationTypeToJSON(AggregationType.COUNT)}>COUNT</option>
                    <option value={aggregationTypeToJSON(AggregationType.UNIQUE_COUNT)}>UNIQUE_COUNT</option>
                    <option value={aggregationTypeToJSON(AggregationType.GROWTH)}>GROWTH</option>
                    <option value={aggregationTypeToJSON(AggregationType.MIN)}>MIN</option>
                    <option value={aggregationTypeToJSON(AggregationType.MAX)}>MAX</option>
                  </Select>
                ) : (
                  <Select value="" disabled>
                    <option value="">None</option>
                  </Select>
                )}
              </Td>
              <Td>
                <DataModelMenuComponent
                  type="KG_COLUMN"
                  logicalColumnList={columns}
                  kgColumnList={kgColumns}
                  selectedItem={dependentKgColumns.get(column.header?.id!)?.[0] || null}
                  columnType={column.column_type}
                  columnIndex={index}
                  logicalColumn={column}
                  onItemSelect={(selectedKgColumn) => {
                    if (selectedKgColumn) {
                      handleKGColumnChange(index, selectedKgColumn.id!, selectedKgColumn);
                    } else {
                      handleKGColumnChange(index, "", null);
                    }
                  }}
                  handleCreateKGColumn={handleCreateKGColumn}
                  handleAddMetricCreationRequest={handleAddMetricCreationRequest}
                  dependentKgColumns={dependentKgColumns}
                  placeholder="None"
                />
              </Td>
              <Td>
                {columns[index].column_type === ColumnType.MEASURE && (
                  <DataModelMenuComponent
                    type="METRIC"
                    updateFlag={updateFlag}
                    modelId={header?.id}
                    logicalColumnList={columns}
                    metricList={getCombinedMetrics(column.header?.id!)}
                    selectedItem={selectedMetrics[index] || null}
                    columnIndex={index}
                    logicalColumn={columns[index]}
                    onItemSelect={(selectedMetric) => handleMetricChange(index, selectedMetric)}
                    handleCreateKGColumn={handleCreateKGColumn}
                    handleAddMetricCreationRequest={handleAddMetricCreationRequest}
                    placeholder="None"
                  />
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* Edit Column Description Modal */}
      <Modal isOpen={isDescriptionModalOpen} onClose={handleCloseDescriptionModal} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Column Description</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Column: <strong>{selectedColumnForDescription !== null ? columns[selectedColumnForDescription]?.header?.name : ""}</strong>
              </Text>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  defaultValue={selectedColumnForDescription !== null ? columns[selectedColumnForDescription]?.header?.description || "" : ""}
                  placeholder="Enter column description..."
                  rows={4}
                  resize="vertical"
                  id="description-textarea"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseDescriptionModal}>
              Cancel
            </Button>
            <Button 
              colorScheme="purple" 
              onClick={() => {
                const textarea = document.getElementById("description-textarea") as HTMLTextAreaElement;
                if (textarea) {
                  handleSaveDescription(textarea.value);
                  handleCloseDescriptionModal();
                }
              }}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
    </Box>
  );
};
export default AddDescription;